/**
 * Who a reply goes to, what it is called, and how the original is quoted.
 * Pure functions: tested in Node (tests/compose.test.js).
 */

const same = (a, b) => String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase()

/**
 * @param {object} message        the message answered (detail: from, to, cc, replyTo)
 * @param {'reply'|'replyAll'|'forward'} mode
 * @param {string[]} own          the person's own addresses: never written to themselves
 * @returns {{to: Array<{name, address}>, cc: Array<{name, address}>}}
 */
export function replyRecipients(message, mode, own = []) {
  if (mode === 'forward') return { to: [], cc: [] }
  const mine = (entry) => own.some(address => same(address, entry?.address))
  const sender = (message.replyTo?.length ? message.replyTo : [message.from]).filter(Boolean)
  // Answering something I sent myself goes back to whom I sent it.
  const fromMe = mine(message.from)
  let to = fromMe ? [...(message.to ?? [])] : sender
  let cc = []
  if (mode === 'replyAll') {
    const others = [...(fromMe ? [] : message.to ?? []), ...(message.cc ?? [])]
    cc = others.filter(entry => !mine(entry) && !to.some(item => same(item.address, entry.address)))
  }
  to = to.filter(entry => entry?.address && (!mine(entry) || fromMe))
  const unique = (list) => list.filter((entry, index) => list.findIndex(item => same(item.address, entry.address)) === index)
  return {
    to: unique(to).map(entry => ({ name: entry.name ?? '', address: String(entry.address).toLowerCase() })),
    cc: unique(cc).map(entry => ({ name: entry.name ?? '', address: String(entry.address).toLowerCase() })),
  }
}

/** «Re: Oferta» and not «Re: Re: Re: Oferta». */
export function replySubject(subject, mode) {
  const base = String(subject ?? '').replace(/^(\s*(re|fw|fwd|rv|tr|aw|wg|sv|reenv)\s*(\[\d+\])?\s*:\s*)+/i, '').trim()
  return `${mode === 'forward' ? 'Fwd' : 'Re'}: ${base}`
}

const escape = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/**
 * The original below the answer: a header line and the body as a quote.
 * `body` is the original's HTML ALREADY CLEANED by the caller (or its text).
 *
 * @param {object} message
 * @param {'reply'|'replyAll'|'forward'} mode
 * @param {object} words           translated: { wrote, forwarded, from, date, subject, to }
 * @param {string} body            cleaned HTML of the original
 * @param {string} dateText        the date as the reader writes it
 */
export function quoteHtml(message, mode, words, body, dateText) {
  const who = message.from?.name ? `${escape(message.from.name)} &lt;${escape(message.from.address)}&gt;` : escape(message.from?.address)
  if (mode === 'forward') {
    const to = (message.to ?? []).map(entry => escape(entry.address)).join(', ')
    return [
      `<p>---------- ${escape(words.forwarded)} ----------<br>`,
      `${escape(words.from)}: ${who}<br>`,
      `${escape(words.date)}: ${escape(dateText)}<br>`,
      `${escape(words.subject)}: ${escape(message.subject)}<br>`,
      `${escape(words.to)}: ${to}</p>`,
      `<div>${body}</div>`,
    ].join('')
  }
  return `<p>${escape(words.wrote.replace('{date}', dateText)).replace('{who}', who)}</p><blockquote style="margin:0 0 0 .8ex;border-left:2px solid #ccc;padding-left:1ex">${body}</blockquote>`
}

/** The editor, the signature and the quote, in the order the recipient reads them. */
export function assemble(body, { signature = '', quote = '' } = {}) {
  // Many people write the «--» into their signature themselves: it is not
  // doubled.
  const delimited = /^(\s*<[^>]+>)*\s*--/.test(signature)
  return [
    body || '',
    signature ? `<br><div>${delimited ? '' : '-- <br>'}${signature}</div>` : '',
    quote ? `<br>${quote}` : '',
  ].join('')
}

/**
 * The «Join the meeting» button of an invitation, above the text the calendar
 * prepared: the calendar writes the link (and no words, §16); the words are
 * the reader's language and come from here. Found by the meeting link's own
 * shape (`/meet/…`), and added once.
 */
export function withJoinButton(html, label) {
  const body = String(html ?? '')
  if (body.includes(`>${escape(label)}</a>`)) return body
  const url = body.match(/href="(https?:\/\/[^"]*\/meet\/[^"]+)"/i)?.[1]
  if (!url) return body
  const button = `<p><a href="${url}" style="display: inline-block; padding: 10px 18px; border-radius: 8px; background-color: #1d4ed8; color: #ffffff; font-weight: 600; text-decoration: none">${escape(label)}</a></p>`
  return button + body
}

const EMAIL = /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:]+$/
export const isAddress = (value) => EMAIL.test(String(value ?? '').trim())
